"use client";

import { useCallback, useMemo } from "react";
import {
  usePathname,
  useRouter,
  useSearchParams,
  // Use the type directly from Next.js for better compatibility
  ReadonlyURLSearchParams,
} from "next/navigation";

// --- Type Definitions ---

/** Scalar types that can be set as a query parameter value. */
export type QueryScalar = string | number | boolean | null | undefined;
/** A single value or an array of scalar values for a query parameter. */
export type QueryValue = QueryScalar | QueryScalar[];
/** An object representing changes to be applied to the query string. */
export type QueryPatch = Record<string, QueryValue>;
/** Options for programmatic navigation. */
export type NavigateOptions = {
  /** If `true`, uses `router.replace` instead of `router.push`. Default is `false`. */
  replace?: boolean;
  /** If `true`, scrolls to the top of the page after navigation. Default is `true`. */
  scroll?: boolean;
};

// --- Helper Functions ---

/**
 * Creates a new, mutable URLSearchParams instance by applying a patch to a base.
 * @param base The initial ReadonlyURLSearchParams.
 * @param patch The object of changes to apply.
 * @returns A new, mutable URLSearchParams instance.
 */
function applyPatch(
  base: ReadonlyURLSearchParams,
  patch: QueryPatch,
): URLSearchParams {
  // Create a mutable copy to work with
  const next = new URLSearchParams(base.toString());

  for (const [key, value] of Object.entries(patch)) {
    // A nullish or empty string value indicates key removal.
    if (value === null || value === undefined || value === "") {
      next.delete(key);
      continue;
    }

    // For arrays, clear the existing key and append each valid value.
    if (Array.isArray(value)) {
      next.delete(key);
      for (const item of value) {
        if (item !== null && item !== undefined) {
          next.append(key, String(item));
        }
      }
      continue;
    }

    // For scalar values, set the key.
    next.set(key, String(value));
  }

  return next;
}

/**
 * Builds a full href string from a pathname and a URLSearchParams instance.
 * @param pathname The URL pathname (e.g., '/dashboard').
 * @param params The URLSearchParams to append.
 * @returns The complete URL string.
 */
function buildHref(pathname: string, params: URLSearchParams): string {
  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

// --- Query Parameter Parsers ---

// Using Sets for efficient, case-insensitive lookups.
const TRUTHY_VALUES = new Set(["1", "true", "yes", "on"]);
const FALSY_VALUES = new Set(["0", "false", "no", "off"]);

/** A collection of utility functions to safely parse query parameters. */
export const queryParsers = {
  /** Parses a value as a string. */
  string(sp: ReadonlyURLSearchParams, key: string, def?: string): string | undefined {
    return sp.get(key) ?? def;
  },
  /** Parses a value as a finite number. */
  number(sp: ReadonlyURLSearchParams, key: string, def?: number): number | undefined {
    const value = sp.get(key);
    if (value === null) return def;
    const num = Number(value);
    return Number.isFinite(num) ? num : def;
  },
  /** Parses a value as a boolean. Recognizes "1", "true", "yes", "on" as true and "0", "false", "no", "off" as false. */
  boolean(sp: ReadonlyURLSearchParams, key: string, def?: boolean): boolean | undefined {
    const value = sp.get(key);
    if (value === null) return def;
    const lowerValue = value.toLowerCase();
    if (TRUTHY_VALUES.has(lowerValue)) return true;
    if (FALSY_VALUES.has(lowerValue)) return false;
    return def;
  },
  /** Parses all occurrences of a key as an array of strings. */
  stringArray(sp: ReadonlyURLSearchParams, key: string): string[] {
    return sp.getAll(key);
  },
  /** Parses all occurrences of a key as an array of finite numbers. */
  numberArray(sp: ReadonlyURLSearchParams, key: string): number[] {
    return sp.getAll(key).map(Number).filter(Number.isFinite);
  },
  /** Case-insensitively parses a value against a list of allowed enum values. */
  enum<T extends readonly string[]>(
    sp: ReadonlyURLSearchParams,
    key: string,
    values: T,
    def?: T[number],
  ): T[number] | undefined {
    const raw = sp.get(key);
    if (raw === null) return def;
    const found = values.find((v) => v.toLowerCase() === raw.toLowerCase());
    return found ?? def;
  },
};

// --- Core Hooks ---

/**
 * A comprehensive hook for managing URL query parameters in Next.js App Router.
 *
 * @example
 * ```tsx
 * const { query, getString, set, remove, clear } = useAppRouting();
 * const search = getString('search', '');
 *
 * return (
 * <input
 * value={search}
 * onChange={(e) => set({ search: e.target.value }, { replace: true })}
 * />
 * );
 * ```
 */
export function useAppRouting() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /**
   * A memoized object representation of the current query string.
   * Correctly handles multiple values for the same key by creating an array.
   */
  const query = useMemo(() => {
    const params: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      if (params.hasOwnProperty(key)) {
        // If key already exists, convert to array
        if (Array.isArray(params[key])) {
          (params[key] as string[]).push(value);
        } else {
          params[key] = [params[key] as string, value];
        }
      } else {
        params[key] = value;
      }
    });
    return params;
  }, [searchParams]);

  /**
   * Navigates to a new URL by applying a patch to the current query string.
   */
  const navigate = useCallback(
    (patch: QueryPatch, options: NavigateOptions = {}) => {
      const { replace = false, scroll = true } = options;
      const newParams = applyPatch(searchParams, patch);
      const href = buildHref(pathname, newParams);

      if (replace) {
        router.replace(href, { scroll });
      } else {
        router.push(href, { scroll });
      }
    },
    [router, pathname, searchParams],
  );

  /** Sets one or more query parameters, then navigates. */
  const set = useCallback(
    (patch: QueryPatch, options?: NavigateOptions) => navigate(patch, options),
    [navigate],
  );

  /** Removes one or more query parameters by key, then navigates. */
  const remove = useCallback(
    (keys: string | string[], options?: NavigateOptions) => {
      const patch = (Array.isArray(keys) ? keys : [keys]).reduce(
        (acc, key) => {
          acc[key] = null; // `applyPatch` interprets null as deletion
          return acc;
        },
        {} as QueryPatch,
      );
      navigate(patch, options);
    },
    [navigate],
  );

  /** Removes all query parameters, then navigates. */
  const clear = useCallback(
    (options: NavigateOptions = {}) => {
      const { replace = false, scroll = true } = options;
      if (replace) {
        router.replace(pathname, { scroll });
      } else {
        router.push(pathname, { scroll });
      }
    },
    [router, pathname],
  );

  /** Generates an href for a link without navigating. */
  const hrefFor = useCallback(
    (patch: QueryPatch): string => {
      const newParams = applyPatch(searchParams, patch);
      return buildHref(pathname, newParams);
    },
    [pathname, searchParams],
  );

  return useMemo(
    () => ({
      // Raw access
      router,
      pathname,
      searchParams,
      // Parsed query object
      query,
      // Typed getters
      getString: (key: string, def?: string) => queryParsers.string(searchParams, key, def),
      getNumber: (key: string, def?: number) => queryParsers.number(searchParams, key, def),
      getBoolean: (key: string, def?: boolean) => queryParsers.boolean(searchParams, key, def),
      getStringArray: (key: string) => queryParsers.stringArray(searchParams, key),
      getNumberArray: (key: string) => queryParsers.numberArray(searchParams, key),
      getEnum: <T extends readonly string[]>(key: string, values: T, def?: T[number]) =>
        queryParsers.enum(searchParams, key, values, def),
      // Write actions
      set,
      remove,
      clear,
      hrefFor,
    }),
    [router, pathname, searchParams, query, set, remove, clear, hrefFor],
  );
}

/**
 * A focused hook to manage a single query parameter with type-safe parsing and serialization.
 *
 * @see useAppRouting for managing multiple parameters at once.
 *
 * @param key The query parameter key.
 * @param options Configuration for parsing, serialization, and navigation behavior.
 * @returns A tuple containing the [currentValue, setValue, removeValue].
 *
 * @example
 * ```tsx
 * // Simple string state
 * const [search, setSearch] = useQueryParam('search');
 *
 * // Number state with a default value
 * const [page, setPage] = useQueryParam('page', {
 * defaultValue: 1,
 * parse: (raw) => raw ? parseInt(raw, 10) : 1,
 * serialize: (value) => value?.toString(),
 * });
 * ```
 */
export function useQueryParam<T>(
  key: string,
  options: {
    parse?: (raw: string | null) => T;
    serialize?: (value: T | undefined | null) => QueryScalar;
    defaultValue?: T;
    replace?: boolean;
  } = {},
) {
  // Destructure options with defaults to stabilize dependencies for callbacks.
  // This prevents re-creating callbacks on every render if an inline options object is passed.
  const {
    parse,
    serialize,
    defaultValue,
    replace = true,
  } = options;

  const { searchParams, set } = useAppRouting();

  const value = useMemo(() => {
    const raw = searchParams.get(key);
    if (parse) {
      // If a parser is provided, it's responsible for handling null/undefined.
      return parse(raw);
    }
    // Default behavior for string-like values.
    return (raw as unknown as T) ?? defaultValue;
  }, [searchParams, key, parse, defaultValue]);

  const setValue = useCallback(
    (newValue: T | undefined | null) => {
      const serializedValue = serialize
        ? serialize(newValue)
        : newValue === null || newValue === undefined ? null : String(newValue);

      set({ [key]: serializedValue }, { replace });
    },
    [key, set, replace, serialize],
  );

  const removeValue = useCallback(() => {
    set({ [key]: null }, { replace });
  }, [key, set, replace]);

  return [value, setValue, removeValue] as const;
}